import React, { useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { FormComponent, ModalComponent, UiComponent } from 'components';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { EventQuery } from 'queries';
import {
  compareApplicationTypes,
  getApplicationTypeDetailLabel,
  IEvent,
} from 'types';
import * as S from './style';

const INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';
const SAVE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

interface Props {
  event: IEvent;
  onClose: () => void;
}

function EditSheet({ event, onClose }: Props) {
  const { mutate: update, isPending } = EventQuery.useUpdate();

  const isLottery = event.recruitmentType === 'LOTTERY';
  const quota = event.applicationTypeQuota;

  const [enrollStart, setEnrollStart] = useState(
    dayjs(event.enrollStartDate).format(INPUT_FORMAT),
  );
  const [enrollEnd, setEnrollEnd] = useState(
    dayjs(event.enrollEndDate).format(INPUT_FORMAT),
  );
  const [participantCount, setParticipantCount] = useState(
    String(event.participantCount ?? ''),
  );
  const [quotaCounts, setQuotaCounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(quota ?? {}).map(([key, value]) => [key, String(value)]),
    ),
  );

  const quotaKeys = Object.keys(quotaCounts).sort(compareApplicationTypes);

  const handleSave = () => {
    if (!enrollStart || !enrollEnd) {
      toast.error('모집기간을 입력해주세요.');
      return;
    }
    if (!dayjs(enrollStart).isBefore(dayjs(enrollEnd))) {
      toast.error('모집 종료일은 시작일 이후여야 합니다.');
      return;
    }

    const counts: Partial<IEvent> = {};
    if (quota) {
      if (quotaKeys.some((key) => !(Number(quotaCounts[key]) > 0))) {
        toast.error('모든 티켓의 인원은 1 이상이어야 합니다.');
        return;
      }
      counts.applicationTypeQuota = Object.fromEntries(
        quotaKeys.map((key) => [key, Number(quotaCounts[key])]),
      );
    } else if (!isLottery) {
      if (!(Number(participantCount) > 0)) {
        toast.error('모집인원을 입력해주세요.');
        return;
      }
      counts.participantCount = Number(participantCount);
    }

    update(
      {
        id: event.id,
        isNewFile: false,
        enrollStartDate: dayjs(enrollStart).format(SAVE_FORMAT),
        enrollEndDate: dayjs(enrollEnd).format(SAVE_FORMAT),
        ...counts,
      },
      {
        onSuccess: () => {
          toast.success('행사 수정이 완료되었습니다.');
          onClose();
        },
        onError: () => toast.error(DEFAULT_ERROR_MESSAGE),
      },
    );
  };

  return (
    <ModalComponent.Portal>
      <ModalComponent.Background>
        <ModalComponent.Modal
          title="기본정보 수정"
          handleClose={onClose}
          footer={<></>}
        >
          <div css={S.sheet}>
            <section css={S.field}>
              <p css={S.label}>모집기간</p>
              <FormComponent.Input
                type="datetime-local"
                value={enrollStart}
                onChange={(e) => setEnrollStart(e.target.value)}
              />
              <FormComponent.Input
                type="datetime-local"
                value={enrollEnd}
                min={enrollStart}
                onChange={(e) => setEnrollEnd(e.target.value)}
              />
            </section>

            <section css={S.field}>
              <p css={S.label}>모집인원</p>
              {isLottery && (
                <p css={S.help}>추첨형은 모집인원 제한이 없습니다.</p>
              )}
              {!isLottery && !quota && (
                <FormComponent.Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={participantCount}
                  onChange={(e) => setParticipantCount(e.target.value)}
                />
              )}
              {quota &&
                quotaKeys.map((key) => (
                  <label key={key} css={S.quotaRow}>
                    <span>
                      {getApplicationTypeDetailLabel(
                        key,
                        event.applicationTypeQuotaTitle,
                      )}
                    </span>
                    <FormComponent.Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={quotaCounts[key]}
                      onChange={(e) =>
                        setQuotaCounts((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </label>
                ))}
            </section>

            <UiComponent.Button
              variant="contained"
              color="primary"
              css={S.saveButton}
              disabled={isPending}
              onClick={handleSave}
            >
              저장
            </UiComponent.Button>
          </div>
        </ModalComponent.Modal>
      </ModalComponent.Background>
    </ModalComponent.Portal>
  );
}

export default EditSheet;
